import { useState, useEffect } from 'react'
import { AiPopUp } from './AiPopUp'
import { AiAgentLogo } from './AiAgentLogo'

interface Step {
  step: number
  completed: boolean
  label: string
  onClick?: () => void
}

interface AiAgentFabWithPopUpProps {
  message?: string
  onOpen?: () => void
  isLoading?: boolean
  hasMessages?: boolean
  isAvailable?: boolean
  steps?: Step[]
}

function AiAgentFab({
  message,
  onOpen,
  isLoading = false,
  hasMessages = false,
  isAvailable = false,
  steps,
}: AiAgentFabWithPopUpProps) {
  const [showSteps, setShowSteps] = useState(!isAvailable)

  useEffect(() => {
    if (isAvailable) {
      // Hide steps after 500ms when agent becomes available
      const timer = setTimeout(() => {
        setShowSteps(false)
      }, 800)

      return () => clearTimeout(timer)
    } else {
      // Show steps immediately when agent becomes unavailable
      setShowSteps(true)
    }
  }, [isAvailable])

  // Show policy required guidance when agent is not available
  if (steps && (!isAvailable || showSteps)) {
    return (
      <div className='ai-agent-steps-required'>
        <div className='ai-agent-steps-required-content'>
          <div className='ai-agent-steps-required-icon'>
            <AiAgentLogo className='ai-agent-avatar' size={60} iconType='head' />
          </div>
          <h3>
            Select policies to chat <br />
            with AL
          </h3>
          <div className='ai-agent-policy-required-steps'>
            {steps.map(({ step, completed, label, onClick }) => (
              <div
                key={step}
                className={`ai-agent-step${completed ? ' ai-agent-step--completed' : ''}`}
                onClick={onClick}
                style={onClick ? { cursor: 'pointer' } : undefined}
              >
                <span className='ai-agent-step-number'>{completed ? '✓' : step}</span>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <AiPopUp message={message} />
      <button
        className={`ai-agent-fab ${isLoading ? 'ai-agent-fab--loading' : ''} ${
          hasMessages ? 'ai-agent-fab--unread' : ''
        }`}
        onClick={onOpen}
        aria-label='Open AI chat'
        type='button'
      >
        <AiAgentLogo className='ai-agent-fab-icon' size={95} iconType='animated' />
        {hasMessages && <div className='ai-agent-fab-unread-indicator' />}
      </button>
    </>
  )
}

export { AiAgentFab }
